import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addMinutes, format, isToday, isTomorrow } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  FileText,
  Loader2,
  PackageCheck,
  RefreshCw,
  ShoppingBag,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import ReceiptDialog from '../../components/ReceiptDialog';
import { useCart } from '../../contexts/CartContext';
import { acceptOnlineOrder, getOrder, getOrders, getTodayCashRegister, updateOnlineOrderStatus } from '../../lib/api';
import {
  extractCustomerOrderNotes,
  getOnlineWorkflowBucket,
  getOnlineWorkflowMeta,
  getOnlineWorkflowStatus,
  getOrderDisplayName,
  getOrderFulfillmentMeta,
  getOnlineConfirmedAt,
  getOnlineRequestedAt,
  getOrderSourceMeta,
  hasAcceptedOnlineOrder,
  isOrderPaid,
  isDeliveryOrder,
  isOnlineOrder,
  isPickupOrder,
} from '../orderMeta';
import { getCashRegisterTimeRange, getCashRegisterUiState } from '../registerState';

const STATUS_FILTERS = [
  { value: 'all', label: 'Alle' },
  { value: 'pending', label: 'Neu' },
  { value: 'accepted', label: 'Angenommen' },
  { value: 'ready', label: 'Bereit' },
  { value: 'finished', label: 'Fertig' },
  { value: 'cancelled', label: 'Storno' },
];

const SOURCE_FILTERS = [
  { value: 'all', label: 'Alle Quellen' },
  { value: 'website', label: 'Webshop' },
  { value: 'foodora', label: 'Foodora' },
  { value: 'lieferando', label: 'Lieferando' },
  { value: 'wolt', label: 'Wolt' },
];

function StatusPill({ status }) {
  const map = {
    open: { label: 'Offen', cls: 'bg-blue-100 text-blue-700' },
    completed: { label: 'Bezahlt', cls: 'bg-emerald-100 text-emerald-700' },
    cancelled: { label: 'Storno', cls: 'bg-red-100 text-red-600' },
    parked: { label: 'Geparkt', cls: 'bg-amber-100 text-amber-700' },
  };
  const current = map[status] || map.open;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${current.cls}`}>
      {current.label}
    </span>
  );
}

function StatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
  if (status === 'cancelled') return <XCircle className="h-5 w-5 text-red-400" />;
  return <Clock className="h-5 w-5 text-blue-400" />;
}

function MetaBadge({ className, label }) {
  return (
    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${className}`}>
      {label}
    </span>
  );
}

function parseDateValue(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return '–';
  return format(parsed, "dd.MM.yyyy 'um' HH:mm", { locale: de });
}

function formatTime(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return '–';
  return parsed.toLocaleTimeString('de-AT', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatServiceLabel(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return 'Sofort';
  if (isToday(parsed)) {
    return `heute um ${format(parsed, 'HH:mm', { locale: de })} Uhr`;
  }
  if (isTomorrow(parsed)) {
    return `morgen um ${format(parsed, 'HH:mm', { locale: de })} Uhr`;
  }
  return format(parsed, "dd.MM. 'um' HH:mm", { locale: de });
}

function fallbackServiceIso(order) {
  const base = parseDateValue(order?.created_at) || new Date();
  const leadMinutes = isDeliveryOrder(order) ? 35 : 20;
  return addMinutes(base, leadMinutes).toISOString();
}

function getRelevantOnlineOrderTimestamp(order) {
  return order?.completed_at || order?.cancelled_at || order?.created_at || '';
}

function getRequestedServiceIso(order) {
  return getOnlineRequestedAt(order) || fallbackServiceIso(order);
}

function getConfirmedServiceIso(order) {
  return getOnlineConfirmedAt(order);
}

function getPaymentState(order) {
  if (isOrderPaid(order)) {
    const paymentLabel = order?.payment_method
      ? `Bezahlt · ${order.payment_method}`
      : order?.payment_status
        ? `Bezahlt · ${order.payment_status}`
        : 'Bezahlt';
    return {
      label: paymentLabel,
      tone: 'text-emerald-700',
      subline: 'Die Bestellung ist bezahlt und bleibt für Verlauf und Abrechnung sichtbar.',
    };
  }

  if (order?.payment_method) {
    return {
      label: `Zahlungsart · ${order.payment_method}`,
      tone: 'text-muted-foreground',
      subline: 'Die Bestellung ist erfasst, aber noch offen.',
    };
  }

  return {
    label: 'Noch nicht bezahlt',
    tone: 'text-rose-600',
    subline: isDeliveryOrder(order)
      ? 'Die Zahlung wird nach der Annahme weiterverarbeitet.'
      : 'Für Abholung bitte erst annehmen und danach kassieren.',
  };
}

function getNextWorkflowAction(order) {
  if (order?.status !== 'open') return null;

  const workflowStatus = getOnlineWorkflowStatus(order);
  if (workflowStatus === 'accepted') {
    return {
      status: 'ready',
      label: isDeliveryOrder(order) ? 'Als bereit markieren' : 'Als abholbereit markieren',
    };
  }
  if (workflowStatus === 'ready') {
    return {
      status: isDeliveryOrder(order) ? 'dispatched' : 'collected',
      label: isDeliveryOrder(order) ? 'An Fahrer übergeben' : 'Als abgeholt markieren',
    };
  }
  if (workflowStatus === 'dispatched') {
    return {
      status: 'delivered',
      label: 'Als zugestellt markieren',
    };
  }
  return null;
}

function OnlineOrdersDetail({
  order,
  onAccept,
  onAdvanceStatus,
  onGoToPayment,
  onReceipt,
  acceptingId,
  advancingId,
  acceptDraftAt,
  acceptNote,
  onAcceptNoteChange,
  onShiftDraft,
}) {
  if (!order) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="text-center text-muted-foreground">
          <ShoppingBag className="mx-auto mb-3 h-14 w-14 opacity-20" />
          <p className="text-sm">Online-Bestellung auswählen</p>
        </div>
      </div>
    );
  }

  const sourceMeta = getOrderSourceMeta(order.order_source);
  const fulfillmentMeta = getOrderFulfillmentMeta(order);
  const workflowMeta = getOnlineWorkflowMeta(order);
  const workflowStatus = getOnlineWorkflowStatus(order);
  const isAccepted = hasAcceptedOnlineOrder(order);
  const isPaid = isOrderPaid(order);
  const requestedAt = getRequestedServiceIso(order);
  const confirmedAt = getConfirmedServiceIso(order);
  const effectiveServiceAt = confirmedAt || acceptDraftAt || requestedAt;
  const paymentState = getPaymentState(order);
  const customerNotes = extractCustomerOrderNotes(order.notes);
  const canTakePayment = order.status === 'open' && !isDeliveryOrder(order) && isAccepted && !isPaid;
  const acceptLabel = isDeliveryOrder(order) ? 'Lieferung annehmen' : 'Abholung annehmen';
  const scheduleLabel = isDeliveryOrder(order)
    ? 'Lieferzeit'
    : isPickupOrder(order)
      ? 'Abholzeit'
      : 'Servicezeit';
  const displayName = getOrderDisplayName(order, 'Kundenbestellung');
  const nextAction = getNextWorkflowAction(order);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-border p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="mb-0.5 text-xs text-muted-foreground">Bestellnummer</p>
            <p className="text-lg font-bold leading-tight text-foreground">
              #{order.order_number?.split('-')[1] ?? order.order_number}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{displayName}</p>
          </div>
          <StatusPill status={order.status} />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
          <MetaBadge className={fulfillmentMeta.className} label={fulfillmentMeta.label} />
          <MetaBadge className={workflowMeta.className} label={workflowMeta.label} />
        </div>

        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
          <p>Eingegangen: {formatDateTime(order.created_at)}</p>
          <p>{scheduleLabel}: {formatServiceLabel(effectiveServiceAt)}</p>
          {order.completed_at && <p>Bezahlt: {formatDateTime(order.completed_at)}</p>}
          {order.cancelled_at && <p>Storniert: {formatDateTime(order.cancelled_at)}</p>}
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {order.status === 'open' && (
          <div className="rounded-[28px] border border-border bg-muted p-4">
            <div className="grid gap-4 xl:grid-cols-[1.35fr,0.85fr]">
              <div className="rounded-[24px] border border-border bg-card p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {scheduleLabel}
                </p>
                <p className="mt-2 text-2xl font-bold text-foreground">
                  {formatServiceLabel(effectiveServiceAt)}
                </p>
                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                  <p>Kundenwunsch: {formatServiceLabel(requestedAt)}</p>
                  {confirmedAt ? <p>Bestätigt: {formatServiceLabel(confirmedAt)}</p> : null}
                </div>
              </div>

              <div className="rounded-[24px] border border-border bg-card p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Zahlung
                </p>
                <p className={`mt-2 text-xl font-bold ${paymentState.tone}`}>
                  {paymentState.label}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">{paymentState.subline}</p>
              </div>
            </div>

            {!isAccepted ? (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-[88px,1fr,88px] gap-2">
                  <button
                    type="button"
                    onClick={() => onShiftDraft(-10)}
                    className="rounded-2xl bg-sky-100 px-3 py-3 text-sm font-semibold text-sky-700 transition-colors hover:bg-sky-200"
                  >
                    -10
                  </button>
                  <div className="flex items-center justify-center rounded-2xl border border-border bg-card px-3 py-3 text-center text-sm font-semibold text-muted-foreground">
                    {formatServiceLabel(acceptDraftAt || requestedAt)}
                  </div>
                  <button
                    type="button"
                    onClick={() => onShiftDraft(10)}
                    className="rounded-2xl bg-sky-500 px-3 py-3 text-sm font-semibold text-white transition-colors hover:bg-sky-600"
                  >
                    +10
                  </button>
                </div>

                <textarea
                  value={acceptNote}
                  onChange={(event) => onAcceptNoteChange(event.target.value)}
                  placeholder="Optionale interne Notiz zur Annahme"
                  className="min-h-[90px] w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground outline-none transition focus:border-border focus:ring-2 focus:ring-slate-200"
                />

                <button
                  onClick={() => onAccept(order)}
                  disabled={acceptingId === order.id}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 text-sm font-semibold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60"
                >
                  {acceptingId === order.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PackageCheck className="h-4 w-4" />
                  )}
                  {acceptLabel}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                  {workflowStatus === 'accepted' && `Bestellung angenommen für ${formatServiceLabel(confirmedAt || requestedAt)}.`}
                  {workflowStatus === 'ready' && (isDeliveryOrder(order)
                    ? 'Bestellung ist vorbereitet und wartet auf den Fahrer.'
                    : 'Bestellung ist abholbereit.')}
                  {workflowStatus === 'collected' && 'Bestellung wurde abgeholt.'}
                  {workflowStatus === 'dispatched' && 'Bestellung ist unterwegs.'}
                  {workflowStatus === 'delivered' && 'Bestellung wurde zugestellt.'}
                  {workflowStatus === 'completed' && 'Bestellung ist abgeschlossen.'}
                </div>

                {nextAction ? (
                  <button
                    onClick={() => onAdvanceStatus(order, nextAction.status)}
                    disabled={advancingId === order.id}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-60"
                  >
                    {advancingId === order.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <PackageCheck className="h-4 w-4" />
                    )}
                    {nextAction.label}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        )}

        {customerNotes ? (
          <div className="rounded-2xl border border-border bg-muted p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Bestellnotiz
            </p>
            <p className="text-sm text-muted-foreground">{customerNotes}</p>
          </div>
        ) : null}

        <div className="space-y-2">
          {order.items?.map((item, index) => (
            <div
              key={`${order.id}-${index}`}
              className="rounded-2xl border border-border bg-card p-3 shadow-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                    {item.quantity}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{item.product_name}</p>
                    {item.notes ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">+ {item.notes}</p>
                    ) : null}
                  </div>
                </div>
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  €{(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-border p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="font-semibold text-muted-foreground">Gesamt</span>
          <span className="text-xl font-bold tabular-nums text-foreground">
            €{order.total?.toFixed(2)}
          </span>
        </div>

        <div className="space-y-2">
          {order.status === 'completed' && (
            <button
              onClick={() => onReceipt(order.id)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-muted text-sm font-medium text-muted-foreground transition-colors hover:bg-slate-200"
            >
              <FileText className="h-4 w-4" />
              Beleg öffnen
            </button>
          )}

          {canTakePayment && (
            <button
              onClick={() => onGoToPayment(order)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-blue-50 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
            >
              <CreditCard className="h-4 w-4" />
              {isPickupOrder(order) ? 'Abholung kassieren' : 'Zur Zahlung'}
            </button>
          )}

          {order.status === 'open' && !canTakePayment && !isDeliveryOrder(order) && !isPaid ? (
            <div className="rounded-2xl border border-border bg-muted px-4 py-3 text-xs text-muted-foreground">
              Zahlung wird erst nach der Annahme freigeschaltet.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function PosOnlineOrders() {
  const navigate = useNavigate();
  const { loadOrder } = useCart();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [register, setRegister] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [acceptingId, setAcceptingId] = useState('');
  const [advancingId, setAdvancingId] = useState('');
  const [acceptDraftAt, setAcceptDraftAt] = useState('');
  const [acceptNote, setAcceptNote] = useState('');

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const activeRegister = await getTodayCashRegister();
      setRegister(activeRegister);

      const timeRange = getCashRegisterTimeRange(activeRegister);
      if (!timeRange) {
        setOrders([]);
        return;
      }

      const registerDate = String(activeRegister?.date || '').replace(/-/g, '');
      const data = await getOrders(null, registerDate || null);
      const windowStart = new Date(timeRange.openedAt).getTime();
      const windowEnd = timeRange.closedAt ? new Date(timeRange.closedAt).getTime() : Number.POSITIVE_INFINITY;

      setOrders(
        data
          .filter((order) => isOnlineOrder(order))
          .filter((order) => {
            const relevantTime = new Date(getRelevantOnlineOrderTimestamp(order) || 0).getTime();
            return relevantTime >= windowStart && relevantTime <= windowEnd;
          })
      );
    } catch {
      toast.error('Online-Bestellungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const registerState = useMemo(() => getCashRegisterUiState(register), [register]);
  const activeShiftRange = useMemo(() => getCashRegisterTimeRange(register), [register]);

  const sourceFilteredOrders = useMemo(() => (
    orders.filter((order) => (
      sourceFilter === 'all' || order.order_source === sourceFilter
    ))
  ), [orders, sourceFilter]);

  const visibleOrders = useMemo(() => (
    sourceFilteredOrders.filter((order) => (
      statusFilter === 'all' || getOnlineWorkflowBucket(order) === statusFilter
    ))
  ), [sourceFilteredOrders, statusFilter]);

  useEffect(() => {
    const nextSelection =
      visibleOrders.find((order) => order.id === selectedOrder?.id) || visibleOrders[0] || null;
    setSelectedOrder(nextSelection);
    setDetailOrder(nextSelection);
  }, [selectedOrder?.id, visibleOrders]);

  useEffect(() => {
    if (!detailOrder) {
      setAcceptDraftAt('');
      setAcceptNote('');
      return;
    }
    setAcceptDraftAt(getConfirmedServiceIso(detailOrder) || getRequestedServiceIso(detailOrder));
    setAcceptNote('');
  }, [detailOrder]);

  const counts = useMemo(() => ({
    pending: sourceFilteredOrders.filter((order) => getOnlineWorkflowBucket(order) === 'pending').length,
    accepted: sourceFilteredOrders.filter((order) => getOnlineWorkflowBucket(order) === 'accepted').length,
    ready: sourceFilteredOrders.filter((order) => getOnlineWorkflowBucket(order) === 'ready').length,
    finished: sourceFilteredOrders.filter((order) => getOnlineWorkflowBucket(order) === 'finished').length,
  }), [sourceFilteredOrders]);

  const handleSelect = (order) => {
    setSelectedOrder(order);
    setDetailOrder(order);
  };

  const handleReceipt = async (orderId) => {
    try {
      const full = await getOrder(orderId);
      setDetailOrder(full);
      setShowReceipt(true);
    } catch {
      toast.error('Beleg konnte nicht geladen werden');
    }
  };

  const handleAccept = async (order) => {
    setAcceptingId(order.id);
    try {
      await acceptOnlineOrder(order.id, {
        note: acceptNote,
        confirmed_fulfillment_at: acceptDraftAt || getRequestedServiceIso(order),
      });
      toast.success('Online-Bestellung angenommen');
      await loadOrders();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Annahme fehlgeschlagen');
    } finally {
      setAcceptingId('');
    }
  };

  const handleAdvanceStatus = async (order, nextStatus) => {
    setAdvancingId(order.id);
    try {
      await updateOnlineOrderStatus(order.id, {
        status: nextStatus,
        note: acceptNote,
        confirmed_fulfillment_at: acceptDraftAt || getConfirmedServiceIso(order) || getRequestedServiceIso(order),
      });
      toast.success('Online-Status aktualisiert');
      await loadOrders();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Status konnte nicht aktualisiert werden');
    } finally {
      setAdvancingId('');
    }
  };

  const handleGoToPayment = async (order) => {
    try {
      const full = await getOrder(order.id);
      loadOrder(full);
      navigate('/pos/terminal', {
        state: {
          openPayment: true,
          paymentIntentId: `${full.id}-${Date.now()}`,
          fulfillmentType: full.fulfillment_type,
          paymentOrder: full,
        },
      });
    } catch {
      toast.error('Bestellung konnte nicht zur Kassa geladen werden');
    }
  };

  const handleShiftDraft = (minutes) => {
    setAcceptDraftAt((previous) => {
      const base = parseDateValue(previous) || parseDateValue(getRequestedServiceIso(detailOrder)) || new Date();
      return addMinutes(base, minutes).toISOString();
    });
  };

  return (
    <div className="flex h-full overflow-hidden bg-muted" data-testid="pos-online-orders">
      <div className="flex w-full flex-shrink-0 flex-col border-r border-border bg-card lg:w-[28rem]">
        <div className="border-b border-border p-4">
          <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-border bg-amber-50 p-3">
              <p className="text-xs text-amber-700">Neu</p>
              <p className="text-xl font-bold text-amber-800">{counts.pending}</p>
            </div>
            <div className="rounded-2xl border border-border bg-sky-50 p-3">
              <p className="text-xs text-sky-700">Angenommen</p>
              <p className="text-xl font-bold text-sky-800">{counts.accepted}</p>
            </div>
            <div className="rounded-2xl border border-border bg-emerald-50 p-3">
              <p className="text-xs text-emerald-700">Bereit</p>
              <p className="text-xl font-bold text-emerald-800">{counts.ready}</p>
            </div>
            <div className="rounded-2xl border border-border bg-violet-50 p-3">
              <p className="text-xs text-violet-700">Fertig</p>
              <p className="text-xl font-bold text-violet-800">{counts.finished}</p>
            </div>
          </div>
        </div>

        <div className="border-b border-border px-3 py-2">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {filter.label}
              </button>
            ))}
            <button
              onClick={loadOrders}
              className="rounded-lg p-1.5 transition-colors hover:bg-muted"
            >
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          <div className="mt-2 flex gap-1 overflow-x-auto pb-1">
            {SOURCE_FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSourceFilter(filter.value)}
                className={`rounded-full px-3 py-1 text-[11px] font-medium whitespace-nowrap transition-colors ${
                  sourceFilter === filter.value
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="border-b border-border px-4 py-2">
          <p className="text-xs text-muted-foreground">
            {isLoading
              ? 'Lädt…'
              : activeShiftRange
                ? `${visibleOrders.length} Online-Bestellung${visibleOrders.length === 1 ? '' : 'en'} im offenen Tag`
                : registerState === 'closed'
                  ? 'Tag beendet. Nach dem nächsten Öffnen startet die Liste leer.'
                  : 'Kein offener Kassentag aktiv.'}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : !activeShiftRange ? (
            <div className="flex h-40 flex-col items-center justify-center px-6 text-center text-muted-foreground">
              <ShoppingBag className="mb-2 h-10 w-10 opacity-30" />
              <p className="text-sm font-semibold">
                {registerState === 'closed' ? 'Tag beendet. Neue Online-Bestellungen erscheinen erst im neuen offenen Tag.' : 'Kein offener Kassentag aktiv.'}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Diese Ansicht zeigt nur Online-Bestellungen innerhalb des aktuell geöffneten Tageszeitraums.
              </p>
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center text-muted-foreground">
              <ShoppingBag className="mb-2 h-10 w-10 opacity-30" />
              <p className="text-sm">Keine Online-Bestellungen</p>
            </div>
          ) : (
            visibleOrders.map((order) => {
              const sourceMeta = getOrderSourceMeta(order.order_source);
              const fulfillmentMeta = getOrderFulfillmentMeta(order);
              const workflowMeta = getOnlineWorkflowMeta(order);
              const requestedAt = getRequestedServiceIso(order);
              const confirmedAt = getConfirmedServiceIso(order);
              const isAccepted = hasAcceptedOnlineOrder(order);
              const displayName = getOrderDisplayName(order, 'Kundenbestellung');

              return (
                <button
                  key={order.id}
                  onClick={() => handleSelect(order)}
                  className={`flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors ${
                    selectedOrder?.id === order.id ? 'bg-muted' : 'hover:bg-muted'
                  }`}
                >
                  <StatusIcon status={order.status} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-foreground">
                        #{order.order_number?.split('-')[1] ?? order.order_number}
                      </p>
                      <p className="text-sm font-bold tabular-nums text-muted-foreground">
                        €{order.total?.toFixed(2)}
                      </p>
                    </div>

                    <div className="mt-1 flex flex-wrap gap-1">
                      <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
                      <MetaBadge className={fulfillmentMeta.className} label={fulfillmentMeta.label} />
                      <MetaBadge className={workflowMeta.className} label={workflowMeta.label} />
                    </div>

                    <div className="mt-1 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-xs text-muted-foreground">
                          {displayName} · {order.items?.filter((item) => !item?.voided).length ?? 0} Artikel
                        </p>
                        <p className="truncate text-[11px] font-medium text-muted-foreground">
                          {isAccepted ? 'Bestätigt' : 'Wunschzeit'}: {formatServiceLabel(confirmedAt || requestedAt)}
                        </p>
                      </div>
                      <p className="flex-shrink-0 text-[10px] text-muted-foreground">
                        {formatTime(order.created_at)}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-300" />
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="hidden flex-1 flex-col overflow-hidden bg-card lg:flex">
        <OnlineOrdersDetail
          order={detailOrder}
          onAccept={handleAccept}
          onAdvanceStatus={handleAdvanceStatus}
          onGoToPayment={handleGoToPayment}
          onReceipt={handleReceipt}
          acceptingId={acceptingId}
          advancingId={advancingId}
          acceptDraftAt={acceptDraftAt}
          acceptNote={acceptNote}
          onAcceptNoteChange={setAcceptNote}
          onShiftDraft={handleShiftDraft}
        />
      </div>

      <ReceiptDialog
        open={showReceipt}
        onClose={() => setShowReceipt(false)}
        order={detailOrder}
      />
    </div>
  );
}
